import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  Button,
  DataTable,
  DatePicker,
  DatePickerInput,
  FilterableMultiSelect,
  Pagination,
  Search,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Add, OpenPanelFilledLeft, Renew } from '@carbon/react/icons';
import { EmptyState } from '@carbon/ibm-products';
import { PageHeader } from 'components/page-header';
import { usePricingEnums } from 'pages/pricing/shared/use-pricing-enums';
import type { Curve, Option } from 'pages/pricing/shared/types';
import { parseUTCdateTime } from 'utils/dates';
import styles from './curve-library.module.scss';

const HEADERS = [
  { key: 'name', header: 'Curve name' },
  { key: 'symbol', header: 'Symbol' },
  { key: 'commodity', header: 'Commodity' },
  { key: 'category', header: 'Category' },
  { key: 'units', header: 'Price unit' },
  { key: 'frequency', header: 'Frequency' },
  { key: 'owner', header: 'Owner' },
  { key: 'createdAt', header: 'Created At' },
  { key: 'updatedAt', header: 'Updated At' },
];

const labelOf = (opts: Option[], value: string | null) =>
  opts.find((o) => o.value === value)?.label ?? value ?? '';

// Backend timestamps are UTC; format them in UTC so the date can't drift a day near midnight.
const fmtDate = (iso: string | null) => (iso ? parseUTCdateTime(iso) : '');

const inRange = (iso: string | null, from: Date | null, to: Date | null) => {
  if (!from && !to) return true;
  if (!iso) return true;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return true;
  if (from && t < from.getTime()) return false;
  if (to) {
    const end = new Date(to);
    end.setHours(23, 59, 59, 999);
    if (t > end.getTime()) return false;
  }
  return true;
};

interface Props {
  curves: Curve[];
  onOpen: (id: number) => void;
  onNew: () => void;
}

export function CurveLibrary({ curves, onOpen, onNew }: Props) {
  const { enums } = usePricingEnums();
  const commodityOpts = useMemo(() => enums?.commodities ?? [], [enums]);
  const categoryOpts = useMemo(() => enums?.categories ?? [], [enums]);

  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [commodities, setCommodities] = useState<string[]>([]);
  const [owners, setOwners] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [updatedFrom, setUpdatedFrom] = useState<Date | null>(null);
  const [updatedTo, setUpdatedTo] = useState<Date | null>(null);

  const allOwners = useMemo(
    () =>
      Array.from(new Set(curves.map((c) => c.owner).filter(Boolean))).sort(),
    [curves]
  ) as string[];

  const createdActive = !!(createdFrom || createdTo);
  const updatedActive = !!(updatedFrom || updatedTo);
  const filterCount = [
    commodities.length > 0,
    owners.length > 0,
    categories.length > 0,
    createdActive,
    updatedActive,
  ].filter(Boolean).length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return curves.filter((c) => {
      const commodity = labelOf(commodityOpts, c.market);
      const category = labelOf(categoryOpts, c.category);
      if (commodities.length > 0 && !commodities.includes(commodity))
        return false;
      if (owners.length > 0 && !owners.includes(c.owner ?? '')) return false;
      if (categories.length > 0 && !categories.includes(category)) return false;
      if (!inRange(c.createdAt, createdFrom, createdTo)) return false;
      if (!inRange(c.updatedAt, updatedFrom, updatedTo)) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) ||
        (c.symbol ?? '').toLowerCase().includes(q) ||
        commodity.toLowerCase().includes(q) ||
        (c.owner ?? '').toLowerCase().includes(q)
      );
    });
  }, [
    curves,
    commodityOpts,
    categoryOpts,
    search,
    commodities,
    owners,
    categories,
    createdFrom,
    createdTo,
    updatedFrom,
    updatedTo,
  ]);

  const rows = useMemo(
    () =>
      filtered.slice((page - 1) * pageSize, page * pageSize).map((c) => ({
        id: String(c.id),
        name: c.name,
        symbol: c.symbol ?? '',
        commodity: labelOf(commodityOpts, c.market),
        category: labelOf(categoryOpts, c.category),
        units: c.units ?? '',
        frequency: labelOf(enums?.frequencies ?? [], c.frequency),
        owner: c.owner ?? '',
        createdAt: fmtDate(c.createdAt),
        updatedAt: fmtDate(c.updatedAt),
      })),
    [filtered, page, pageSize, commodityOpts, categoryOpts, enums]
  );

  const resetFilters = () => {
    setCommodities([]);
    setOwners([]);
    setCategories([]);
    setCreatedFrom(null);
    setCreatedTo(null);
    setUpdatedFrom(null);
    setUpdatedTo(null);
    setPage(1);
  };
  const clearCreated = () => {
    setCreatedFrom(null);
    setCreatedTo(null);
    setPage(1);
  };
  const clearUpdated = () => {
    setUpdatedFrom(null);
    setUpdatedTo(null);
    setPage(1);
  };

  const toggleLabel =
    filterCount > 0
      ? `${filterCount} Filter${filterCount > 1 ? 's' : ''} Applied`
      : showFilters
        ? 'Hide Filters'
        : 'Show Filters';

  return (
    <div className={styles.list}>
      <PageHeader
        breadcrumbs={[]}
        title="Curve Management"
        actions={
          <Button kind="primary" size="lg" renderIcon={Add} onClick={onNew}>
            New curve
          </Button>
        }
      />

      <section className={styles.main}>
        {showFilters && (
          <aside className={styles.filterPanel}>
            <div className={styles.filterHeader}>
              <Button
                kind="ghost"
                size="sm"
                renderIcon={Renew}
                disabled={filterCount === 0}
                onClick={resetFilters}
              >
                Reset Filters
              </Button>
            </div>

            <Accordion>
              <AccordionItem title="Date Filters" open>
                <div className={styles.dateGroup}>
                  <span className={styles.groupLabel}>Created At</span>
                  <DatePicker
                    datePickerType="range"
                    value={[createdFrom, createdTo].filter(Boolean) as Date[]}
                    onChange={(dates: Date[]) => {
                      setCreatedFrom(dates[0] ?? null);
                      setCreatedTo(dates[1] ?? null);
                      setPage(1);
                    }}
                  >
                    <DatePickerInput
                      id="cm-created-from"
                      placeholder="mm/dd/yyyy"
                      labelText="From"
                      size="sm"
                    />
                    <DatePickerInput
                      id="cm-created-to"
                      placeholder="mm/dd/yyyy"
                      labelText="To"
                      size="sm"
                    />
                  </DatePicker>
                  <Button
                    kind="ghost"
                    size="sm"
                    disabled={!createdActive}
                    onClick={clearCreated}
                  >
                    Clear
                  </Button>
                </div>
                <div className={styles.dateGroup}>
                  <span className={styles.groupLabel}>Updated At</span>
                  <DatePicker
                    datePickerType="range"
                    value={[updatedFrom, updatedTo].filter(Boolean) as Date[]}
                    onChange={(dates: Date[]) => {
                      setUpdatedFrom(dates[0] ?? null);
                      setUpdatedTo(dates[1] ?? null);
                      setPage(1);
                    }}
                  >
                    <DatePickerInput
                      id="cm-updated-from"
                      placeholder="mm/dd/yyyy"
                      labelText="From"
                      size="sm"
                    />
                    <DatePickerInput
                      id="cm-updated-to"
                      placeholder="mm/dd/yyyy"
                      labelText="To"
                      size="sm"
                    />
                  </DatePicker>
                  <Button
                    kind="ghost"
                    size="sm"
                    disabled={!updatedActive}
                    onClick={clearUpdated}
                  >
                    Clear
                  </Button>
                </div>
              </AccordionItem>
            </Accordion>

            <Accordion>
              <AccordionItem title="Curve Details" open>
                <div className={styles.filterGroup}>
                  <FilterableMultiSelect
                    id="cm-f-commodity"
                    titleText="Commodity"
                    placeholder="Choose commodities"
                    items={commodityOpts.map((o) => o.label)}
                    initialSelectedItems={commodities}
                    selectionFeedback="top-after-reopen"
                    size="sm"
                    onChange={({ selectedItems }) => {
                      setCommodities((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <FilterableMultiSelect
                    id="cm-f-owner"
                    titleText="Owner"
                    placeholder="Choose owners"
                    items={allOwners}
                    initialSelectedItems={owners}
                    selectionFeedback="top-after-reopen"
                    size="sm"
                    onChange={({ selectedItems }) => {
                      setOwners((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
                <div className={styles.filterGroup}>
                  <FilterableMultiSelect
                    id="cm-f-category"
                    titleText="Category"
                    placeholder="Choose categories"
                    items={categoryOpts.map((o) => o.label)}
                    initialSelectedItems={categories}
                    selectionFeedback="top-after-reopen"
                    size="sm"
                    onChange={({ selectedItems }) => {
                      setCategories((selectedItems as string[]) ?? []);
                      setPage(1);
                    }}
                  />
                </div>
              </AccordionItem>
            </Accordion>
          </aside>
        )}

        <div className={styles.tableArea}>
          <div className={styles.toolbar}>
            <Button
              kind="ghost"
              renderIcon={OpenPanelFilledLeft}
              onClick={() => setShowFilters((v) => !v)}
            >
              {toggleLabel}
            </Button>
            <Search
              size="lg"
              labelText="Search curves"
              placeholder="Search by name, symbol, commodity, or owner"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              onClear={() => setSearch('')}
              className={styles.toolbarSearch}
            />
          </div>

          <div className={styles.tableContainer}>
            {filtered.length === 0 ? (
              <EmptyState
                size="lg"
                title="No curves found"
                subtitle={
                  filterCount > 0 || search.length > 0
                    ? 'Try adjusting your filters or search terms.'
                    : 'You don’t have any curves yet. Create one to get started.'
                }
                action={
                  filterCount > 0 || search.length > 0
                    ? {
                        text: 'Reset filters',
                        onClick: () => {
                          resetFilters();
                          setSearch('');
                        },
                      }
                    : { text: 'New curve', onClick: onNew }
                }
              />
            ) : (
              <DataTable rows={rows} headers={HEADERS}>
                {({
                  rows: tableRows,
                  headers,
                  getTableProps,
                  getHeaderProps,
                  getRowProps,
                }) => (
                  <Table {...getTableProps()} size="lg">
                    <TableHead>
                      <TableRow>
                        {headers.map((h) => {
                          const props = getHeaderProps({ header: h });
                          const { key, ...rest } = props;
                          return (
                            <TableHeader key={key ?? h.key} {...rest}>
                              {h.header}
                            </TableHeader>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableRows.map((row) => {
                        const rowProps = getRowProps({ row });
                        const { key, ...rest } = rowProps;
                        return (
                          <TableRow key={key ?? row.id} {...rest}>
                            {row.cells.map((cell) => {
                              if (cell.info.header === 'name') {
                                return (
                                  <TableCell key={cell.id}>
                                    <button
                                      type="button"
                                      className={styles.nameLink}
                                      onClick={() => onOpen(Number(row.id))}
                                    >
                                      {cell.value}
                                    </button>
                                  </TableCell>
                                );
                              }
                              return (
                                <TableCell key={cell.id}>
                                  {cell.value}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </DataTable>
            )}
          </div>

          <Pagination
            backwardText="Previous page"
            forwardText="Next page"
            itemsPerPageText="Curves per page:"
            totalItems={filtered.length}
            pageSize={pageSize}
            pageSizes={[25, 50, 75, 100]}
            page={page}
            onChange={({ page: p, pageSize: ps }) => {
              setPage(p);
              setPageSize(ps);
            }}
          />
        </div>
      </section>
    </div>
  );
}
